import React, { useState, useEffect, useMemo } from "react";
import { TextField, CircularProgress, Autocomplete, List, IconButton } from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { styled } from "@mui/system";
import colors from "assets/theme-dark/base/colors";
import { useViewsQuery } from "../../../../graphql/types/graphql";
import PromoteDialog from "./PromoteDialog";
import Divider from "@mui/material/Divider";

interface HeaderSectionProps {
  outputTableName: string;
  setOutputTableName: React.Dispatch<React.SetStateAction<string>>;
  handleMerge: () => void;
  handlePromote: (name: string) => void;
  loading: boolean;
  isPromoteButtonDisabled: boolean;
  selectedCubeId: string;
  viewPage: number;
  setViewPage: React.Dispatch<React.SetStateAction<number>>;
  viewPageSize: number;
  setIsReplacingView?: React.Dispatch<React.SetStateAction<boolean>>;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  outputTableName,
  setOutputTableName,
  handleMerge,
  handlePromote,
  loading,
  isPromoteButtonDisabled,
  selectedCubeId,
  viewPage,
  setViewPage,
  viewPageSize,
  setIsReplacingView,
}) => {
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteName, setPromoteName] = useState("");
  const [selectedView, setSelectedView] = useState<string | null>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [allFetchedViews, setAllFetchedViews] = useState<string[]>([]);

  const {
    data,
    loading: viewsLoading,
    error,
    fetchMore,
  } = useViewsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        cubeId: selectedCubeId,
      },
      limit: viewPageSize,
      offset: viewPage * viewPageSize,
    },
  });

  useEffect(() => {
    if (data?.views?.edges) {
      const newViewNames = data.views.edges.map((view) => view.name);
      setAllFetchedViews((prev) => {
        const updatedViews = Array.from(new Set([...prev, ...newViewNames]));
        return updatedViews;
      });
    }
  }, [data]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allFetchedViews;
    return allFetchedViews.filter((view) => view.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [allFetchedViews, searchTerm]);

  const handleFetchMore = () => {
    if (!viewsLoading && data?.views?.hasNextPage) {
      setViewPage((prev) => prev + 1);
      fetchMore({
        variables: {
          offset: (viewPage + 1) * viewPageSize,
        },
      });
    }
  };

  const handleFetchPrevious = () => {
    if (!viewsLoading && viewPage > 0) {
      setViewPage((prev) => prev - 1);
      fetchMore({
        variables: {
          offset: (viewPage - 1) * viewPageSize,
        },
      });
    }
  };

  const handlePromoteSubmit = (name: string) => {
    handlePromote(name);
    setPromoteDialogOpen(false);
    setPromoteName("");
  };

  const isInputDisabled = !!selectedView;

  return (
    <StyledCard sx={{ mb: 2 }}>
      <MDTypography variant="h4" sx={{ color: colors.text.main, mb: 1 }}>
        Merge Datasets
      </MDTypography>
      <Divider
        sx={{
          margin: 0,
          marginBottom: "10px",
          borderColor: colors.grey[500],
          borderWidth: "1px",
        }}
      />
      <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <MDBox>
          <MDTypography variant="subtitle2" sx={{ color: colors.text.main, fontSize: "0.85rem", mt: 1 }}>
            Select datasets below to merge or promote.
          </MDTypography>
        </MDBox>
        <MDBox sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <MDTypography variant="subtitle2" sx={{ color: colors.text.main, fontSize: "0.85rem" }}>
            Select a view to replace:
          </MDTypography>
          <Autocomplete
            options={filteredOptions}
            value={selectedView}
            onChange={(event, newValue) => {
              setSelectedView(newValue);
              if (newValue) {
                setIsReplacingView(true);
                setOutputTableName(newValue);
              } else {
                setIsReplacingView(false);
                setOutputTableName("");
              }
            }}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select View"
                variant="outlined"
                size="small"
                sx={{
                  width: "300px",
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: colors.text.main },
                    "&:hover fieldset": { borderColor: colors.primary.main },
                    "&.Mui-focused fieldset": { borderColor: colors.primary.main },
                    color: colors.text.main,
                  },
                  "& .MuiInputBase-input": { color: colors.text.main },
                  "& .MuiInputLabel-root": { color: colors.grey[900] },
                  "& .MuiInputLabel-root.Mui-focused": { color: colors.grey[500] },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {viewsLoading ? <CircularProgress size={20} sx={{ color: colors.text.main }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onOpen={() => setViewPage(0)}
            ListboxComponent={(props) => (
              <CustomListbox {...props}>
                {viewPage > 0 && !viewsLoading && (
                  <LoadPreviousContainer>
                    <IconButton
                      onClick={handleFetchPrevious}
                      sx={{
                        color: colors.primary.main,
                        "&:hover": { backgroundColor: colors.grey[300] },
                      }}
                      aria-label="Load previous views"
                    >
                      <ArrowUpward />
                    </IconButton>
                  </LoadPreviousContainer>
                )}
                {props.children}
                {data?.views?.hasNextPage && !viewsLoading && (
                  <LoadMoreContainer>
                    <IconButton
                      onClick={handleFetchMore}
                      sx={{
                        color: colors.primary.main,
                        "&:hover": { backgroundColor: colors.grey[300] },
                      }}
                      aria-label="Load more views"
                    >
                      <ArrowDownward />
                    </IconButton>
                  </LoadMoreContainer>
                )}
              </CustomListbox>
            )}
          />
          <MDTypography variant="subtitle2" sx={{ color: colors.text.main, fontSize: "0.85rem" }}>
            Merge two or more datasets:
          </MDTypography>
          <MDBox sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <TextField
              label="Output Table Name"
              variant="outlined"
              size="small"
              value={outputTableName}
              onChange={(e) => {
                setOutputTableName(e.target.value);
                setIsReplacingView(false);
                setSelectedView(null);
              }}
              disabled={isInputDisabled}
              sx={{
                width: "300px",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderWidth: "0.5px", borderColor: colors.text.main },
                  "&:hover fieldset": { borderColor: colors.primary.main },
                  "&.Mui-focused fieldset": { borderColor: colors.primary.main },
                  color: colors.text.main,
                },
                "& .MuiInputBase-input": { color: colors.text.main },
                "& .MuiInputLabel-root": { color: colors.grey[500] },
                "& .MuiInputLabel-root.Mui-focused": { color: colors.grey[500] },
              }}
            />
            <MDButton
              variant="contained"
              color="secondary"
              disabled={!outputTableName?.trim() || loading}
              onClick={handleMerge}
            >
              {loading ? <CircularProgress size={24} sx={{ color: colors.text.main }} /> : "Merge"}
            </MDButton>
          </MDBox>
          <MDTypography variant="subtitle2" sx={{ color: colors.text.main, fontSize: "0.85rem", mt: "1rem" }}>
            Promote one dataset to matcher:
          </MDTypography>
          <MDBox sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <MDButton
              variant="contained"
              color="secondary"
              disabled={loading || isPromoteButtonDisabled || isInputDisabled}
              onClick={() => setPromoteDialogOpen(true)}
            >
              {loading ? <CircularProgress size={24} sx={{ color: colors.text.main }} /> : "Promote"}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
      <PromoteDialog
        open={promoteDialogOpen}
        onClose={() => setPromoteDialogOpen(false)}
        onSubmit={handlePromoteSubmit}
        promoteName={promoteName}
        setPromoteName={setPromoteName}
      />
    </StyledCard>
  );
};

const StyledCard = styled(MDBox)`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

const CustomListbox = styled(List)(({ theme }) => ({
  position: "relative",
  paddingTop: "40px",
  paddingBottom: "40px",
  "& .MuiAutocomplete-option": {
    color: colors.text.main,
    "&:hover": {
      color: colors.primary.main,
      backgroundColor: colors.grey[300],
    },
  },
}));

const LoadMoreContainer = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  display: "flex",
  justifyContent: "center",
}));

const LoadPreviousContainer = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  display: "flex",
  justifyContent: "center",
}));

export default HeaderSection;
